import { Component, OnDestroy, WritableSignal, computed, effect, input, model, signal, viewChild } from "@angular/core"
import { NgClass, KeyValuePipe } from "@angular/common"
import { toSignal } from "@angular/core/rxjs-interop"
import { tap } from "rxjs"
import { DependenciesService, EmployeeService } from "src/app/services";
import { MatTree, MatTreeNode, MatTreeNodeDef, MatTreeNodePadding, MatTreeNodeToggle } from "@angular/material/tree";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatIcon } from "@angular/material/icon";
import { EdpData, PositionNode, Node, EmployeeSubType, EmployeeType, EmployeeMbrad } from "src/app/models";
import { MatMenu, MatMenuTrigger, MatMenuItem } from '@angular/material/menu';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatRadioGroup, MatRadioButton } from "@angular/material/radio";
import { TreeFilterComponent } from "./tree-filter.component";
import { CostCentersFilterComponent } from "./cost-centers.component";
import { EdpDataNamesPipe, OrgUnitTreePipe } from "src/app/pipes";
import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport, CdkVirtualForOf } from "@angular/cdk/scrolling";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTab, MatTabContent, MatTabGroup } from "@angular/material/tabs";
import { EmployeesPickListComponent } from "./employees-pick-list.component";
import { MatStepper, MatStep, MatStepLabel } from "@angular/material/stepper";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { dependencyGender } from "src/app/helpers/DependencyGender";

type Filter = "orgUnits" | "positions" | "costCenters" | "employeeTypes" | "genders";
type FilterGroup = Record<Filter, string[] | null>;
const FILTER_TIME = 500;

@Component({
    selector: "valben-filters",
    templateUrl: "filters.component.html",
    standalone: true,
    imports: [
        MatProgressSpinner,
        CdkFixedSizeVirtualScroll,
        EmployeesPickListComponent,
        CdkVirtualScrollViewport,
        CdkVirtualForOf,
        MatRadioGroup,
        MatRadioButton,
        NgClass,
        MatInput,
        MatTabGroup,
        MatTab,
        MatStepper,
        MatStep,
        MatStepLabel,
        MatTabContent,
        MatTree,
        MatTreeNode,
        MatTreeNodeDef,
        MatFormField,
        MatTreeNodeToggle,
        MatTreeNodePadding,
        MatIcon,
        MatCheckbox,
        MatIcon,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatButton,
        MatRadioGroup,
        MatIconButton,
        KeyValuePipe,
        TreeFilterComponent,
        CostCentersFilterComponent,
        EdpDataNamesPipe,
        OrgUnitTreePipe,
    ],
    styles: [`
        :host {
            position: relative;
        }
    `],
})
export class FiltersComponent implements OnDestroy {
    public error = input<boolean>(false);
    public value = model.required<{
        filterGroups: FilterGroup[],
        includedEmployees: EmployeeMbrad[],
        excludedEmployees: EmployeeMbrad[],
    }>();

    private readonly _pickListViewport = viewChild<EmployeesPickListComponent>("pickList");
    private readonly _employeeViewport = viewChild(CdkVirtualScrollViewport);
    private readonly _employeesLoading = signal<boolean>(true);
    private readonly _dependenciesLoading = signal<boolean>(true);
    protected readonly loading = computed(() => this._employeesLoading() || this._dependenciesLoading());

    protected employees = toSignal(
        this._employeeService.employees$.pipe(tap(() => this._employeesLoading.set(false))),
        { initialValue: [] },
    );
    protected dependencies = toSignal(
        this._dependenciesService.dependencies$.pipe(tap(() => this._dependenciesLoading.set(false))),
        {
            initialValue: {
                orgUnits: null,
                orgUnitTypes: [],
                positions: [],
                costCenters: [],
                employeeTypes: [],
            } as EdpData,
        },
    );
    protected filteredDependencies = computed(() => {
        const filterEdpData = this.filterEdpData();
        const filtering = this.filtering();
        const dependencies = this.dependencies();

        if (!filterEdpData || !filtering)
            return {
                positions: dependencies.positions ?? [],
                costCenters: dependencies.costCenters ?? [],
                orgUnits: dependencies.orgUnits?.children ?? [],
            };

        const groups = this.value().filterGroups;
        const employees = this.employees();

        if (groups.every(
            g => !g.costCenters && !g.orgUnits && !g.positions)
        ) {
            return {
                costCenters: dependencies.costCenters ?? [],
                orgUnits: dependencies.orgUnits?.children ?? [],
                positions: dependencies.positions ?? [],
            };
        }

        const validOrgUnits = new Set<string>();
        const validPositions = new Set<string>();
        const validCostCenters = new Set<string>();
        const toSet = (arr: string[] | null) => arr && arr.length > 0 ? new Set(arr) : null;

        groups.forEach(group => {
            const orgUnits = toSet(group.orgUnits);
            const positions = toSet(group.positions);
            const costCenters = toSet(group.costCenters);

            for (let i=0; i < employees.length; i++) {
                const hasOrgUnit = filtering === "orgUnits"
                    ? true : !orgUnits || orgUnits.has(employees[i].orgUnitId);
                const hasPosition = filtering === "positions"
                    ? true : !positions || positions.has(employees[i].positionId);
                const hasCostCenter = filtering === "costCenters"
                    ? true : !costCenters || costCenters.has(employees[i].costCenterId);

                if (hasOrgUnit && hasPosition && hasCostCenter) {
                    validOrgUnits.add(employees[i].orgUnitId);
                    validCostCenters.add(employees[i].costCenterId);
                    validPositions.add(employees[i].positionId);
                }
            }
        });

        return {
            costCenters: dependencies.costCenters?.filter(c => validCostCenters.has(c.masterId)) ?? [],
            orgUnits: this._filterTree(dependencies.orgUnits?.children, validOrgUnits, this.getOrgUnitId) ?? [],
            positions: this._filterTree(dependencies.positions, validPositions, this.getPositionId) ?? [],
        };
    });

    protected employeeSearch = signal<string>("");
    protected filtering: WritableSignal<Filter | null> = signal(null);
    protected selected = signal<string[]>([]);
    protected activeGroupIdx: number = -1;
    protected orgUnitsNames: Map<string, string> | undefined;
    protected employeeTypesNames: Map<string, string> | undefined;
    protected positionsNames: Map<string, string> | undefined;
    protected costCentersNames: Map<string, string> | undefined;
    protected gendersNames: Map<string, string> | undefined;
    protected filteredEmployees = signal<EmployeeMbrad[]>([]);
    protected filterEdpData = signal<boolean>(false);
    protected orgUnitType = signal<"all" | "ops" | "cs">("all");

    // Šifrarnici nude samo aktivne šifre, pa zaposlenik s neaktivnom ili neponuđenom šifrom
    // ostaje izvan filtra i kad je označeno sve što se nudi.
    protected uncovered = computed(() => {
        const dependencies = this.dependencies();

        const offered =
            this.filtering() === "orgUnits"
                ? new Set(this._flatTree(dependencies.orgUnits ? [dependencies.orgUnits] : []).map(this.getOrgUnitId))
            : this.filtering() === "positions"
                ? new Set(this._flatTree(dependencies.positions ?? []).map(this.getPositionId))
            : this.filtering() === "costCenters"
                ? new Set((dependencies.costCenters ?? []).map(c => c.masterId))
            : null;

        if (!offered) return 0;

        const employeeId = (employee: EmployeeMbrad) =>
            this.filtering() === "orgUnits" ? employee.orgUnitId
            : this.filtering() === "positions" ? employee.positionId
            : employee.costCenterId;

        return this.employees().filter(e => !offered.has(employeeId(e))).length;
    });

    protected getPositionLabel = (node: PositionNode) => node.position.names["hr"] ?? "";
    protected getPositionId = (node: PositionNode) => node.position.masterId;
    protected getOrgUnitLabel = (node: Node) => node.orgUnit.names["hr"] ?? "";
    protected getOrgUnitId = (node: Node) => node.orgUnit.masterId;
    protected getEmployeeTypeLabel = (node: EmployeeType | EmployeeSubType) => node.names["hr"] ?? "";
    protected getEmployeeTypeId = (node: EmployeeType | EmployeeSubType) => node.masterId;

    private _filterTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private _employeeService: EmployeeService,
        private _dependenciesService: DependenciesService,
    ) {
        effect(() => {
            const depends = this.dependencies();

            this.positionsNames = new Map(
                this._flatTree(depends.positions ?? []).map((x: PositionNode) => [x.position.masterId, x.position.names["hr"] ?? ""])
            );
            this.orgUnitsNames = new Map(
                this._flatTree(depends.orgUnits ? [depends.orgUnits] : []).map((x: Node) => [x.orgUnit.masterId, x.orgUnit.names["hr"] ?? ""])
            );
            this.employeeTypesNames = new Map(
                this._flatTree(depends.employeeTypes ?? []).map(t => [t.masterId, t?.names["hr"] ?? ""])
            );
            this.costCentersNames = new Map(
                (depends.costCenters ?? []).map(c => [c.masterId, c.names["hr"] ?? "Nepoznato"])
            );
            this.gendersNames = new Map([["M", "Muško"], ["Z", "Žensko"]]);
        });

        effect(() => {
            const v = this.value();
            const f = this.filtering();
            const s = this.selected();
            const es = this.employeeSearch();
            this.employees();

            clearTimeout(this._filterTimer);

            if (!f) return;

            this._filterTimer = setTimeout(() => {
                this.filteredEmployees.set(this._filterEmployees(v.filterGroups, f, s, es));
                setTimeout(() => this._employeeViewport()?.checkViewportSize());
            }, FILTER_TIME);
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this._filterTimer);
    }

    protected addFilterGroup(): void {
        this.value.update(value => ({
            ...value,
            filterGroups: [
                ...value.filterGroups,
                {
                    orgUnits: null,
                    positions: null,
                    genders: null,
                    costCenters: null,
                    employeeTypes: null,
                }
            ],
        }));
    }

    protected deleteFilterGroup(i: number): void {
        this.filtering.set(null);
        this.selected.set([]);
        this.activeGroupIdx = -1;
        this.value.update(value => {
            const copy = [...value.filterGroups];
            copy.splice(i, 1);
            return { ...value, filterGroups: copy };
        });
    }

    protected addFilter(filter: Filter, i: number): void {
        this.activeGroupIdx = i;
        this.filtering.set(filter);
        this.selected.set(this.value().filterGroups[i][filter as Filter] ?? []);
    }

    protected discardFilter(): void {
        this.activeGroupIdx = -1;
        this.filtering.set(null);
        this.selected.set([]);
    }

    protected saveFilter(): void {
        const filtering = this.filtering()!;

        this.value.update(value => {
            const copy = [...value.filterGroups];
            copy[this.activeGroupIdx] = { ...copy[this.activeGroupIdx], [filtering]: this.selected() };
            return { ...value, filterGroups: copy };
        });

        this.filtering.set(null);
        this.selected.set([]);
        this.activeGroupIdx = -1;
    }

    protected removeFilter(filter: string, i: number): void {
        this.value.update(value => {
            const copy = [...value.filterGroups];
            copy[i] = { ...copy[i], [filter as Filter]: null };
            return { ...value, filterGroups: copy };
        });
        this.activeGroupIdx = -1;
    }

    protected editFilter(filter: string, i: number): void {
        this.filtering.set(filter as Filter);
        this.activeGroupIdx = i;
        this.selected.set(this.value().filterGroups[i][filter as Filter] ?? []);
    }

    private _filterEmployees(
        groups: FilterGroup[],
        filtering: Filter | null,
        selected: string[],
        search: string,
    ): EmployeeMbrad[] {
        const employees = this.employees();
        const names = new Set<EmployeeMbrad>();
        // Prazan popis je spremljen filtar bez odabira i ne obuhvaća nikoga; null znači da filtar nije postavljen.
        const toSet = (arr: string[] | null) => arr ? new Set(arr) : null;

        const effective = groups.map((group, i) =>
            filtering && i === this.activeGroupIdx ? { ...group, [filtering]: selected } : group);

        if (effective.every(
            g => !g.genders && !g.costCenters && !g.orgUnits && !g.employeeTypes && !g.positions)
        )
            return search
                ? employees.filter(x => `${x.fullName} ${x.mbrad}`.toLowerCase().includes(search ?? ""))
                : employees;

        effective.forEach(group => {
            const genders = toSet(group.genders?.map(dependencyGender) ?? null);
            const orgUnits = toSet(group.orgUnits);
            const positions = toSet(group.positions);
            const costCenters = toSet(group.costCenters);
            const employeeTypes = toSet(group.employeeTypes);

            for (const employee of employees) {
                const hasGender = !genders || genders.has(dependencyGender(employee.gender));
                const hasOrgUnit = !orgUnits || orgUnits.has(employee.orgUnitId);
                const hasPosition = !positions || positions.has(employee.positionId);
                const hasCostCenter = !costCenters || costCenters.has(employee.costCenterId);
                const hasTypes = !employeeTypes || employeeTypes.has(employee.employeeTypeId) || employeeTypes.has(employee.employeeSubTypeId);

                if (hasGender && hasOrgUnit && hasPosition && hasCostCenter && hasTypes)
                    names.add(employee);
            }
        });

        const ret = [...names];
        return search
            ? ret.filter(x => `${x.fullName} ${x.mbrad}`.toLowerCase().includes(search ?? ""))
            : ret;
    }

    private _flatTree(nodes: any[]): any[] {
        return nodes.flatMap(node => [node, ...this._flatTree(node.children ?? [])]);
    }

    private _filterTree(
        nodes: any[] | null | undefined,
        validIds: Set<string>,
        getId: (node: any) => string,
    ): any[] {
        if (!nodes) return [];

        const filterNode = (node: any): any => {
            const children = (node.children ?? []).map(filterNode).filter((c: any) => c!== null);
            const id = getId(node);
            return !validIds.has(id) && children.length === 0 ? null : { ...node, children: children };
        };

        return nodes.map(filterNode).filter(n => n !== null);
    }

    protected onStepChange(event: StepperSelectionEvent): void {
        if (event.selectedIndex === 1) {
            const inMap = new Map(this._filterEmployees(
                this.value().filterGroups, null, [], ""
            ).map(x => [x.masterId, x]));

            this.pickEmployees.set({
                in: [...inMap.values()],
                out: this.employees().filter(x => !inMap.has(x.masterId)),
                exIn: this.value().includedEmployees,
                exOut: this.value().excludedEmployees,
            });
        }
    }

    protected onEmit(value: { in: EmployeeMbrad[], ex: EmployeeMbrad[] }): void {
        this.value.set({
            filterGroups: [...this.value().filterGroups],
            includedEmployees: value.in,
            excludedEmployees: value.ex,
        });
    }

    pickEmployees = signal<{
        in: EmployeeMbrad[], out: EmployeeMbrad[], exIn: EmployeeMbrad[], exOut: EmployeeMbrad[],
    }>({ in: [], out: [], exIn: [], exOut: [] });
}
